import { Command } from "commander";
import chalk from "chalk";
import ora from "ora";
import { checkNode } from "../checks/node.js";
import { checkPackageManagers } from "../checks/packageManagers.js";
import { checkJava } from "../checks/java.js";
import { checkAndroid } from "../checks/android.js";
import { checkIos } from "../checks/ios.js";
import { checkReactNative } from "../checks/reactNative.js";
import { CheckResult } from "../checks/types.js";
import { resolvePackageManager } from "../packageManager/packageManagerService.js";
import { detectProjectType } from "../project/projectDetector.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

export const doctorCommand = new Command("doctor")
  .description("Check React Native environment and project configuration")
  .action(async () => {
    console.log();
    const spinner = ora("Running environment and project checks...").start();

    try {
      const nodeResults = await checkNode();
      const pmResults = await checkPackageManagers();
      const javaResults = await checkJava();
      const androidResults = await checkAndroid();
      const iosResults = await checkIos();
      const projectResults = await checkReactNative(process.cwd());

      // Load plugin doctor checks
      try {
        const loadedPlugins = await loadPluginsForProject(process.cwd());
        for (const plugin of loadedPlugins) {
          if (plugin.doctorChecks) {
            const checks = await plugin.doctorChecks(process.cwd());
            projectResults.push(...checks);
          }
        }
      } catch {}

      spinner.succeed("Checks completed!");
      console.log();
      console.log(chalk.bold.underline("React Native Health Report"));
      console.log(`Project Type: ${chalk.cyan(detectProjectType(process.cwd()))}`);
      console.log();

      const envResults = [
        ...nodeResults,
        ...javaResults,
        ...androidResults,
        ...iosResults,
      ];

      const formatResult = (res: CheckResult) => {
        let symbol = "";
        if (res.status === "success") {
          symbol = chalk.green("✓");
        } else if (res.status === "info") {
          symbol = chalk.blue("ℹ");
        } else if (res.status === "warning") {
          symbol = chalk.yellow("⚠");
        } else {
          symbol = chalk.red("✖");
        }
        // Left align and pad the names to 15 characters for PMs, 30 for env/project
        const isPM = ["npm", "yarn", "pnpm", "bun"].includes(res.name);
        const namePart = res.name.padEnd(isPM ? 15 : 30);
        return `  ${symbol} ${namePart} ${res.message}`;
      };

      console.log(chalk.bold("Environment"));
      for (const res of envResults) {
        console.log(formatResult(res));
      }
      console.log();

      console.log(chalk.bold("Package Managers"));
      for (const res of pmResults) {
        console.log(formatResult(res));
      }
      console.log();

      console.log(chalk.bold("Selected"));
      const selectedPM = await resolvePackageManager(process.cwd());
      console.log(`  ${chalk.green("✓")} ${selectedPM.selected}`);
      console.log();

      console.log(chalk.bold("Project"));
      for (const res of projectResults) {
        console.log(formatResult(res));
      }
      console.log();

      const allResults = [...envResults, ...pmResults, ...projectResults];
      const passed = allResults.filter((r) => r.status === "success").length;
      const warnings = allResults.filter((r) => r.status === "warning").length;
      const errors = allResults.filter((r) => r.status === "error").length;

      console.log(chalk.bold("Summary"));
      console.log(`Passed:   ${chalk.green(passed)}`);
      console.log(`Warnings: ${chalk.yellow(warnings)}`);
      console.log(`Errors:   ${chalk.red(errors)}`);
      console.log();

      const suggestions = allResults.filter(
        (r) => (r.status === "warning" || r.status === "error") && r.suggestion
      );

      if (suggestions.length > 0) {
        console.log(chalk.bold.yellow("Suggestions"));
        for (const res of suggestions) {
          const prefix = res.status === "error" ? chalk.red("•") : chalk.yellow("•");
          console.log(`${prefix} ${chalk.bold(res.name)} (${res.status}):`);
          console.log(`  ${res.suggestion}`);
          console.log();
        }
      }

      if (errors > 0) {
        try {
          const reportPath = await generateDiagnosticBundle("doctor", new Error(`Doctor completed with ${errors} error checks.`));
          console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
        } catch {}
      }

      // Exit with 1 if there are any error status checks, otherwise 0
      process.exit(errors > 0 ? 1 : 0);
    } catch (err: any) {
      spinner.fail("An unexpected error occurred during checks.");
      console.error(err);
      try {
        const reportPath = await generateDiagnosticBundle("doctor", err);
        console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
      } catch {}
      process.exit(1);
    }
  });